/**
 * Admin · Growth: landing visits, signups and call retention for each site
 * (GET /admin/growth, see growth.py). Landing visits come from the beacon in
 * the landing pages' attribution.js since the day it was deployed, and from
 * Google Analytics 4 before that.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, GrowthReport, GrowthSite, GrowthSiteMetrics } from './adminApi';
import './Admin.css';

const WINDOWS = [7, 30, 90, 365];
const SITES: { id: GrowthSite; label: string }[] = [
  { id: 'all', label: 'Both sites' },
  { id: 'jp', label: 'callout.jp' },
  { id: 'us', label: 'heycallout.com' },
];

function pct(part: number, whole: number): string {
  return whole ? `${Math.round((100 * part) / whole)}%` : '—';
}

/** Every day from `since` to today (UTC), so empty days show as gaps. */
export function daysFrom(since: string, until: Date = new Date()): string[] {
  const out: string[] = [];
  const end = until.toISOString().slice(0, 10);
  for (let d = new Date(`${since}T00:00:00Z`); out.length < 400; d.setUTCDate(d.getUTCDate() + 1)) {
    const day = d.toISOString().slice(0, 10);
    if (day > end) break;
    out.push(day);
  }
  return out;
}

function Card({ value, label, title }: { value: string; label: string; title?: string }) {
  return (
    <div className="admin-card" title={title}>
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

type Tip = { x: number; y: number; text: string } | null;

function Tooltip({ tip }: { tip: Tip }) {
  if (!tip) return null;
  return (
    <div className="growth-tooltip" style={{ left: tip.x, top: tip.y }}>
      {tip.text}
    </div>
  );
}

/** Where a tooltip goes for a pointer event, relative to the chart box. */
function tipAt(e: React.MouseEvent, text: string): Tip {
  const box = (e.currentTarget as Element).closest('.growth-chart')!.getBoundingClientRect();
  return { x: e.clientX - box.left + 12, y: e.clientY - box.top + 12, text };
}

// ---------------------------------------------------------------------------
// Sankey: new visitors → signed up → first call → called again
// ---------------------------------------------------------------------------

interface SankeyNode {
  id: string;
  label: string;
  value: number;
  col: number;
  dropOff?: boolean;
}

interface SankeyLink {
  from: string;
  to: string;
  value: number;
}

/** The funnel as Sankey nodes and links. Signups can outnumber the visitors
 * counted (visits before tracking started, sign-ups straight on the app,
 * blocked beacons); those come from a separate "not via a tracked visit"
 * node so no flow is drawn wider than its source. */
export function funnelSankey(f: GrowthSiteMetrics['funnel']): { nodes: SankeyNode[]; links: SankeyLink[] } {
  const fromVisits = Math.min(f.signups, f.newVisitors);
  const untracked = f.signups - fromVisits;
  const nodes: SankeyNode[] = [
    { id: 'visitors', label: 'New visitors', value: f.newVisitors, col: 0 },
    { id: 'untracked', label: 'Not via a tracked visit', value: untracked, col: 0, dropOff: true },
    { id: 'signups', label: 'Signed up', value: f.signups, col: 1 },
    { id: 'left', label: 'Didn’t sign up', value: f.newVisitors - fromVisits, col: 1, dropOff: true },
    { id: 'called', label: 'Made a call', value: f.called, col: 2 },
    { id: 'nocall', label: 'No call yet', value: f.signups - f.called, col: 2, dropOff: true },
    { id: 'again', label: 'Called again', value: f.calledAgain, col: 3 },
    { id: 'once', label: 'One call', value: f.called - f.calledAgain, col: 3, dropOff: true },
  ].filter((n) => n.value > 0);
  const links: SankeyLink[] = [
    { from: 'visitors', to: 'signups', value: fromVisits },
    { from: 'untracked', to: 'signups', value: untracked },
    { from: 'visitors', to: 'left', value: f.newVisitors - fromVisits },
    { from: 'signups', to: 'called', value: f.called },
    { from: 'signups', to: 'nocall', value: f.signups - f.called },
    { from: 'called', to: 'again', value: f.calledAgain },
    { from: 'called', to: 'once', value: f.called - f.calledAgain },
  ].filter((l) => l.value > 0);
  return { nodes, links };
}

const SK = { width: 900, height: 300, nodeWidth: 12, gap: 16, colX: [0, 250, 500, 750], minNode: 3 };

function Sankey({ funnel }: { funnel: GrowthSiteMetrics['funnel'] }) {
  const [tip, setTip] = useState<Tip>(null);
  const [hover, setHover] = useState<string | null>(null);
  const { nodes, links } = funnelSankey(funnel);
  if (nodes.length === 0) return <div className="admin-muted">No visitors or signups in this window.</div>;

  // One scale for every column, from the fullest one.
  const colTotals = [0, 1, 2, 3].map((c) => nodes.filter((n) => n.col === c).reduce((s, n) => s + n.value, 0));
  const colCounts = [0, 1, 2, 3].map((c) => nodes.filter((n) => n.col === c).length);
  const scale = Math.min(
    ...[0, 1, 2, 3]
      .filter((c) => colTotals[c] > 0)
      .map((c) => (SK.height - SK.gap * (colCounts[c] - 1)) / colTotals[c])
  );
  const h = (v: number) => Math.max(SK.minNode, v * scale);

  const pos: Record<string, { x: number; y: number; h: number; outY: number; inY: number }> = {};
  for (let c = 0; c < 4; c++) {
    let y = 0;
    for (const n of nodes.filter((n) => n.col === c)) {
      pos[n.id] = { x: SK.colX[c], y, h: h(n.value), outY: y, inY: y };
      y += h(n.value) + SK.gap;
    }
  }
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const paths = links.map((l) => {
    const a = pos[l.from];
    const b = pos[l.to];
    const w = Math.max(1, l.value * scale);
    const x0 = a.x + SK.nodeWidth;
    const x1 = b.x;
    const y0 = a.outY + w / 2;
    const y1 = b.inY + w / 2;
    a.outY += w;
    b.inY += w;
    const mx = (x0 + x1) / 2;
    return { link: l, w, d: `M${x0},${y0} C${mx},${y0} ${mx},${y1} ${x1},${y1}` };
  });

  // The share each node is of the node it came from.
  const parentOf: Record<string, string> = { left: 'visitors', called: 'signups', nocall: 'signups', again: 'called', once: 'called' };
  const share = (n: SankeyNode) => {
    if (n.id === 'signups') return funnel.newVisitors ? `${pct(funnel.signups, funnel.newVisitors)} of visitors` : '';
    const parent = byId[parentOf[n.id]];
    return parent ? pct(n.value, parent.value) : '';
  };

  return (
    <div className="growth-chart">
      <svg
        viewBox={`-2 -8 ${SK.width + 150} ${SK.height + 16}`}
        className="growth-svg"
        role="img"
        aria-label={`Funnel: ${funnel.newVisitors} new visitors, ${funnel.signups} signed up, ${funnel.called} made a call, ${funnel.calledAgain} called again`}
        onMouseLeave={() => {
          setTip(null);
          setHover(null);
        }}
      >
        {paths.map(({ link, w, d }) => {
          const key = `${link.from}-${link.to}`;
          const drop = byId[link.to].dropOff || byId[link.from].dropOff;
          return (
            <path
              key={key}
              d={d}
              className={`growth-link ${drop ? 'drop' : ''} ${hover && hover !== key ? 'dim' : ''}`}
              strokeWidth={w}
              onMouseMove={(e) => {
                setHover(key);
                setTip(tipAt(e, `${byId[link.from].label} → ${byId[link.to].label}: ${link.value.toLocaleString()}`));
              }}
            />
          );
        })}
        {nodes.map((n) => {
          const p = pos[n.id];
          return (
            <g key={n.id}>
              <rect
                x={p.x}
                y={p.y}
                width={SK.nodeWidth}
                height={p.h}
                rx={2}
                className={`growth-node ${n.dropOff ? 'drop' : ''}`}
              />
              <text x={p.x + SK.nodeWidth + 6} y={p.y + p.h / 2 - 2} className="growth-node-label">
                {n.label}
              </text>
              <text x={p.x + SK.nodeWidth + 6} y={p.y + p.h / 2 + 12} className="growth-node-value">
                {n.value.toLocaleString()}
                {share(n) ? ` · ${share(n)}` : ''}
              </text>
            </g>
          );
        })}
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily bars (one measure per chart: visitors and signups differ in scale)
// ---------------------------------------------------------------------------

const BARS = { width: 900, height: 120, left: 36, bottom: 18 };

/** Accounts that signed up but have no profile: the app couldn't create it
 *  (e.g. firestore.rules refused it), so the person can't use the app. */
function MissingProfiles({ report }: { report: GrowthReport }) {
  const missing = report.missingProfiles;
  if (report.missingProfilesError) {
    return (
      <p className="admin-muted" style={{ marginTop: 0 }}>
        Accounts without a profile not checked ({report.missingProfilesError}).
      </p>
    );
  }
  if (missing.length === 0) {
    return (
      <p className="admin-muted" style={{ marginTop: 0 }}>
        Every account created in this window has a profile.
      </p>
    );
  }
  return (
    <div className="admin-error">
      <details>
        <summary>
          {missing.length.toLocaleString()} account{missing.length === 1 ? '' : 's'} created in this window{' '}
          {missing.length === 1 ? 'has' : 'have'} no profile, so {missing.length === 1 ? 'it isn’t' : 'they aren’t'}{' '}
          counted as signups and can’t use the app (any site).
        </summary>
        <ul className="growth-missing">
          {missing.map((a) => (
            <li key={a.uid}>
              {a.email ?? a.uid} · {new Date(a.createdAt).toLocaleString()}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function fmtDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function DailyBars({
  title,
  days,
  values,
  unit,
  ga4,
}: {
  title: string;
  days: string[];
  values: number[];
  unit: string;
  /** Days whose value came from GA4, drawn as outlined light bars. */
  ga4?: boolean[];
}) {
  const [tip, setTip] = useState<Tip>(null);
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...values);
  const plotW = BARS.width - BARS.left;
  const step = plotW / Math.max(1, days.length);
  const barW = Math.max(1, step - 2);
  const y = (v: number) => BARS.height * (1 - v / max);
  const total = values.reduce((s, v) => s + v, 0);
  const tickEvery = Math.max(1, Math.ceil(days.length / 6));

  return (
    <div className="growth-chart">
      <h3 className="growth-chart-title">
        {title} <span className="admin-muted">· {total.toLocaleString()} total</span>
        {ga4?.some(Boolean) && (
          <span className="growth-legend">
            <span className="growth-swatch" /> Beacon
            <span className="growth-swatch ga4" /> Google Analytics
          </span>
        )}
      </h3>
      <svg
        viewBox={`0 0 ${BARS.width} ${BARS.height + BARS.bottom}`}
        className="growth-svg"
        role="img"
        aria-label={`${title}: ${total} over ${days.length} days`}
        onMouseLeave={() => {
          setTip(null);
          setHover(null);
        }}
      >
        <line x1={BARS.left} x2={BARS.width} y1={BARS.height} y2={BARS.height} className="growth-axis" />
        <line x1={BARS.left} x2={BARS.width} y1={0} y2={0} className="growth-grid" />
        <text x={BARS.left - 6} y={8} className="growth-tick" textAnchor="end">
          {max.toLocaleString()}
        </text>
        <text x={BARS.left - 6} y={BARS.height} className="growth-tick" textAnchor="end">
          0
        </text>
        {days.map((day, i) => {
          const v = values[i];
          const x = BARS.left + i * step + 1;
          const top = y(v);
          const r = Math.min(4, barW / 2, BARS.height - top);
          return (
            <g key={day}>
              {/* Hit target: the whole column, not just the bar. */}
              <rect
                x={BARS.left + i * step}
                y={0}
                width={step}
                height={BARS.height}
                fill="transparent"
                onMouseMove={(e) => {
                  setHover(i);
                  setTip(tipAt(e, `${fmtDay(day)}: ${v.toLocaleString()} ${unit}${ga4?.[i] ? ' (GA4)' : ''}`));
                }}
              />
              {v > 0 && (
                <path
                  className={`growth-bar ${ga4?.[i] ? 'ga4' : ''} ${hover !== null && hover !== i ? 'dim' : ''}`}
                  pointerEvents="none"
                  d={`M${x},${BARS.height} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${BARS.height} Z`}
                />
              )}
              {i % tickEvery === 0 && (
                <text x={x + barW / 2} y={BARS.height + 14} className="growth-tick" textAnchor="middle">
                  {fmtDay(day)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly cohorts
// ---------------------------------------------------------------------------

function CohortTable({ cohorts }: { cohorts: GrowthSiteMetrics['cohorts'] }) {
  const weeks = cohorts.length;
  return (
    <div className="admin-table-wrap">
      <table className="admin-table static growth-cohorts">
        <thead>
          <tr>
            <th>Signup week</th>
            <th>Users</th>
            {Array.from({ length: weeks }, (_, n) => (
              <th key={n}>Week {n}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.week}>
              <td>{fmtDay(c.week)}</td>
              <td>{c.users}</td>
              {Array.from({ length: weeks }, (_, n) => {
                if (n >= c.retained.length) return <td key={n} />;
                if (!c.users) return <td key={n} className="admin-muted">—</td>;
                const share = c.retained[n] / c.users;
                return (
                  <td
                    key={n}
                    className={`growth-cell ${share > 0.5 ? 'strong' : ''}`}
                    style={{ '--share': `${Math.round(share * 100)}%` } as React.CSSProperties}
                    title={`${c.retained[n]} of ${c.users} made a call in week ${n}`}
                  >
                    {Math.round(share * 100)}%
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data);
  return (
    <div className="admin-breakdown">
      <h3>{title}</h3>
      {entries.length === 0 ? (
        <div className="admin-muted">No data</div>
      ) : (
        entries.map(([k, v]) => (
          <div className="row" key={k}>
            <span>{k}</span>
            <span>{v.toLocaleString()}</span>
          </div>
        ))
      )}
    </div>
  );
}

function AdminGrowth() {
  const [params, setParams] = useSearchParams();
  const days = WINDOWS.includes(Number(params.get('days'))) ? Number(params.get('days')) : 30;
  const site = (SITES.find((s) => s.id === params.get('site'))?.id || 'all') as GrowthSite;
  const [report, setReport] = useState<GrowthReport | null>(null);
  const [error, setError] = useState('');
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError('');
    adminApi
      .growth(days)
      .then((r) => !cancelled && setReport(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load growth'));
    return () => {
      cancelled = true;
    };
  }, [days, reloads]);

  const setParam = (key: string, value: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'growth');
      next.set(key, value);
      return next;
    });

  const m = report?.sites[site];
  const dayList = report ? daysFrom(report.since) : [];
  const f = m?.funnel;

  return (
    <div className="admin-section" style={{ marginTop: 0 }}>
      <div className="admin-tabs" style={{ marginBottom: 16, alignItems: 'center' }}>
        {SITES.map((s) => (
          <button
            key={s.id}
            className={`admin-inline-btn ${site === s.id ? 'active' : ''}`}
            onClick={() => setParam('site', s.id)}
          >
            {s.label}
          </button>
        ))}
        <span className="admin-muted" style={{ margin: '0 4px' }}>|</span>
        {WINDOWS.map((d) => (
          <button
            key={d}
            className={`admin-inline-btn ${days === d ? 'active' : ''}`}
            onClick={() => setParam('days', String(d))}
          >
            {d === 365 ? '1 year' : `${d} days`}
          </button>
        ))}
        <button className="admin-inline-btn" onClick={() => setReloads((n) => n + 1)} disabled={!report}>
          Reload
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {!report && !error && <div className="admin-muted">Loading…</div>}

      {report && m && f && (
        <>
          {report.ga4Until && !report.ga4Error && (
            <p className="admin-muted" style={{ marginTop: 0 }}>
              Landing visits through {fmtDay(report.ga4Until)} are from Google Analytics (new users, in the property’s
              time zone){report.trackingSince ? `; from ${fmtDay(report.trackingSince)} on, from the landing pages’ own beacon` : ''}.
            </p>
          )}
          {report.ga4Error && (
            <div className="admin-error">
              Google Analytics history not loaded ({report.ga4Error}).{' '}
              {report.trackingSince
                ? `Landing visits are counted from ${fmtDay(report.trackingSince)}; earlier signups have no counted visit.`
                : 'No landing visits counted by the beacon yet.'}
            </div>
          )}

          <MissingProfiles report={report} />

          <div className="admin-cards">
            <Card value={f.newVisitors.toLocaleString()} label="New landing visitors" title="First visit on this browser (no first-touch cookie yet)" />
            <Card value={f.signups.toLocaleString()} label="Signups" />
            <Card value={pct(f.signups, f.newVisitors)} label="Visitor → signup" />
            <Card value={pct(f.called, f.signups)} label="Signup → first call" />
            <Card value={pct(f.calledAgain, f.called)} label="First call → called again" />
          </div>

          <h2>Funnel</h2>
          <p className="admin-muted" style={{ marginTop: 0 }}>
            Users who signed up in the window, and whether they have made a call since.
          </p>
          <Sankey funnel={f} />

          <h2>Per day</h2>
          <DailyBars
            title="New landing visitors"
            days={dayList}
            values={dayList.map((d) => m.landingsByDay[d]?.newVisitors || 0)}
            ga4={dayList.map((d) => !!m.landingsByDay[d]?.ga4)}
            unit="new visitors"
          />
          <DailyBars
            title="Signups"
            days={dayList}
            values={dayList.map((d) => m.signupsByDay[d] || 0)}
            unit="signups"
          />

          <div className="admin-breakdowns">
            <Breakdown title="Landing pages (views)" data={m.topPages} />
            <Breakdown title="Sources (new visitors)" data={m.topSources} />
          </div>

          <h2>Retention</h2>
          <p className="admin-muted" style={{ marginTop: 0 }}>
            Share of each week’s signups who started a call in week 0, 1, … after signing up (UTC weeks from Monday;
            a week shows once everyone in the cohort has finished it). Not limited to the window above.
          </p>
          <CohortTable cohorts={m.cohorts} />
        </>
      )}
    </div>
  );
}

export default AdminGrowth;
